import { defineStore } from 'pinia';
import { useDeliveryStore } from './delivery';
import { DELIVERY_EXPRESS, DELIVERY_ID } from '@/config/delivery-config';
import type { Address, UpdateAddressError } from '@/types/address';

export const useAddressStore = defineStore('address', () => {
  const DEFAULT_SEARCH_PARAMS = {
    search: '',
  };

  const addresses = ref<Address[]>([]);
  const searchParams = ref<{ [key: string]: string }>({ ...DEFAULT_SEARCH_PARAMS });
  const addressesCached = ref<boolean>(false);
  const isGettingAddresses = ref<boolean>(false);

  const setAddressesCached = (value: boolean): void => {
    addressesCached.value = value;
  };

  const upsertAddress = (address: Address): void => {
    const addressIndexToUpdate = addresses.value.findIndex((addr) => addr.addressId === address.addressId);

    if (addressIndexToUpdate === -1) {
      addresses.value.unshift(address);
      return;
    }

    addresses.value.splice(addressIndexToUpdate, 1, address);
  };

  const fetchAddresses = async (): Promise<Address | { error?: boolean }> => {
    if (addressesCached.value) return { error: undefined };
    isGettingAddresses.value = true;
    const { $authService } = useNuxtApp();
    const { data } = await $authService.getAddresses();
    isGettingAddresses.value = false;
    if (!data) {
      return { error: true };
    }
    const formattedAddresses = data.deliveryAddresses.map((address) => {
      if (address.readOnly) {
        return formattedAddress(address);
      }
      return address;
    });

    addresses.value = formattedAddresses;
    addressesCached.value = true;
    return { error: undefined };
  };

  const clearDeliveryAddress = (): void => {
    const { setDeliveryAddressForDeliveryId } = useDeliveryStore();
    setDeliveryAddressForDeliveryId(DELIVERY_EXPRESS, {});
    setDeliveryAddressForDeliveryId(DELIVERY_ID, {});
  };

  const setPreselectedAddress = async (
    address: Address,
    clearDelivery = true,
  ): Promise<{ updated?: boolean; error?: unknown }> => {
    const { $sentry } = useNuxtApp();
    try {
      let addressToUpdate: Address | undefined;
      const previouslySelected = addresses.value.find((address) => address.preselected);
      if (address.readOnly && !previouslySelected) return { updated: false };
      if (address.readOnly && previouslySelected) addressToUpdate = { ...previouslySelected, preselected: false };
      else addressToUpdate = { ...address, preselected: !address.preselected };
      await saveAddress(addressToUpdate, true);
      if (previouslySelected) upsertAddress({ ...previouslySelected, preselected: false });
      if (clearDelivery) clearDeliveryAddress();
      return { updated: true };
    } catch (error) {
      $sentry.captureException(error);
      return { error };
    }
  };

  const saveAddress = async (address: Address, update = false): Promise<UpdateAddressError | undefined> => {
    const { $authService } = useNuxtApp();
    const payload = { ...address };
    delete payload.countryCode;
    const action = update ? $authService.updateAddress : $authService.createAddress;
    const { error, data } = await action(payload, address.addressId);
    if (error) return error;
    if (data) upsertAddress(data);
  };

  const deleteAddress = async (addressId: string): Promise<UpdateAddressError | undefined> => {
    const { $authService } = useNuxtApp();
    const { error } = await $authService.deleteAddress(addressId);
    if (error) return error;
    const addressIndexToDelete = addresses.value.findIndex((address) => address.addressId === addressId);
    addresses.value.splice(addressIndexToDelete, 1);
  };

  const patchSearchParams = (payload: { [key: string]: string }): void => {
    searchParams.value = { ...searchParams.value, ...payload };
  };

  const includeAddress = (address: Address, search: string): boolean => {
    const searchStrings = search ? search.split(' ').map((searchString) => searchString.toUpperCase()) : [];
    if (searchStrings.length === 0) {
      return true;
    }

    const label = address.label ? address.label.toUpperCase() : '';
    const name = address.name ? address.name.toUpperCase() : '';
    const recipient = address.recipientName ? address.recipientName.toUpperCase() : '';
    const addr1 = address.address1 ? address.address1.toUpperCase() : '';
    const addr2 = address.address2 ? address.address2.toUpperCase() : '';
    const addr3 = address.address3 ? address.address3.toUpperCase() : '';
    const zip = address.postalCode ? address.postalCode.toUpperCase() : '';
    const city = address.postalAddress ? address.postalAddress.toUpperCase() : '';

    return searchStrings.every(
      (searchStr) =>
        label.includes(searchStr) ||
        name.includes(searchStr) ||
        recipient.includes(searchStr) ||
        addr1.includes(searchStr) ||
        addr2.includes(searchStr) ||
        addr3.includes(searchStr) ||
        zip.includes(searchStr) ||
        city.includes(searchStr),
    );
  };

  const getSearchParams = computed<{ [key: string]: string }>(() => {
    return searchParams.value;
  });

  const getAddresses = computed<Address[]>(() => {
    return [...addresses.value].filter((address) => includeAddress(address, searchParams.value.search));
  });

  const getTotalAddresses = computed<number>(() => {
    return addresses.value.length;
  });

  const getPreselectedAddress = computed<Address | undefined>(() => {
    let preSelectedAddress = addresses.value.find((address) => {
      return address.preselected;
    });
    if (!preSelectedAddress) {
      preSelectedAddress = addresses.value.find((address) => address.readOnly);
    }
    return preSelectedAddress;
  });

  return {
    setAddressesCached,
    fetchAddresses,
    clearDeliveryAddress,
    setPreselectedAddress,
    saveAddress,
    deleteAddress,
    patchSearchParams,
    isGettingAddresses,
    getSearchParams,
    addressesCached,
    addresses,
    getAddresses,
    getPreselectedAddress,
    getTotalAddresses,
  };
});
